import React, { useEffect, useState } from 'react';
import { Download, Share, X } from 'lucide-react';

/**
 * Floating button that lets the user "install" DATAPRO as a Progressive Web
 * App on their device. Works on:
 *
 *   - Chrome / Edge / Brave on Android & desktop: native `beforeinstallprompt`
 *     event, fires a real install dialog with the app icon.
 *   - iOS Safari (and any iOS browser that uses WebKit): no programmatic
 *     install, so we show a small instruction sheet telling the user to tap
 *     the Share button → "Add to Home Screen".
 *
 * The button hides itself completely when:
 *   - the app is already running in standalone mode (it has been installed),
 *   - the user has dismissed it in this session,
 *   - the browser has no install mechanism we can detect.
 */
export default function InstallAppButton() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isStandalone, setIsStandalone] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [showIOSSheet, setShowIOSSheet] = useState(false);

  useEffect(() => {
    // Detect standalone (already installed)
    const standalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;
    setIsStandalone(standalone);

    // Detect iOS Safari / WebKit
    const ua = window.navigator.userAgent;
    const ios = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    setIsIOS(ios);

    // Capture the install prompt for Android/Chrome/Edge
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);

    // If the user installs, hide the button forever
    const onInstalled = () => {
      setDeferredPrompt(null);
      setIsStandalone(true);
    };
    window.addEventListener('appinstalled', onInstalled);

    // Honour the in-session dismiss
    if (sessionStorage.getItem('datapro_install_dismissed') === '1') {
      setDismissed(true);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const dismiss = () => {
    setDismissed(true);
    sessionStorage.setItem('datapro_install_dismissed', '1');
  };

  const handleInstall = async () => {
    if (isIOS) {
      setShowIOSSheet(true);
      return;
    }
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') setDeferredPrompt(null);
    }
  };

  // Visibility rules
  if (isStandalone || dismissed) return null;
  // For non-iOS browsers that didn't fire beforeinstallprompt we have no
  // way to install — hide the button entirely so we never confuse users.
  if (!isIOS && !deferredPrompt) return null;

  return (
    <>
      <button
        type="button"
        onClick={handleInstall}
        data-testid="install-app-btn"
        aria-label="Instalar como app"
        className="fixed bottom-5 left-5 z-40 flex items-center gap-2 px-4 h-11 rounded-full
                   bg-red-600 hover:bg-red-700 text-white shadow-lg shadow-red-900/40
                   transition active:scale-95 border border-red-500/40"
      >
        <Download className="w-4 h-4" />
        <span className="text-sm font-semibold whitespace-nowrap">Instalar como app</span>
        <span
          role="button"
          tabIndex={0}
          onClick={(e) => { e.stopPropagation(); dismiss(); }}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); dismiss(); } }}
          data-testid="install-app-dismiss"
          className="ml-1 -mr-1 w-5 h-5 inline-flex items-center justify-center rounded-full
                     hover:bg-red-800/70 cursor-pointer"
          title="Ocultar"
        >
          <X className="w-3.5 h-3.5" />
        </span>
      </button>

      {showIOSSheet && (
        <div
          className="fixed inset-0 z-50 bg-black/70 flex items-end justify-center sm:items-center"
          onClick={() => setShowIOSSheet(false)}
          data-testid="install-ios-sheet"
        >
          <div
            className="bg-neutral-900 border border-neutral-800 rounded-t-2xl sm:rounded-2xl
                       p-5 max-w-sm w-full m-0 sm:m-4 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2 mb-3">
              <img src="/icon-192.png" alt="" className="w-9 h-9 rounded" />
              <div className="flex-1">
                <div className="text-white font-semibold">Instalar DATAPRO</div>
                <div className="text-[11px] text-gray-400">Agregar a la pantalla de inicio</div>
              </div>
              <button
                type="button"
                onClick={() => setShowIOSSheet(false)}
                className="text-gray-500 hover:text-white"
                data-testid="install-ios-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <ol className="text-sm text-gray-300 space-y-3 mt-2">
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-red-600 text-white text-xs flex items-center justify-center font-bold">1</span>
                <span>
                  Tocá el botón <Share className="w-4 h-4 inline align-text-bottom text-blue-400" /> Compartir
                  en la barra inferior de Safari.
                </span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-red-600 text-white text-xs flex items-center justify-center font-bold">2</span>
                <span>Deslizá hasta encontrar <strong>"Agregar a pantalla de inicio"</strong>.</span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-red-600 text-white text-xs flex items-center justify-center font-bold">3</span>
                <span>Tocá <strong>Agregar</strong>. Listo: DATAPRO queda en tu pantalla de inicio.</span>
              </li>
            </ol>
          </div>
        </div>
      )}
    </>
  );
}
